import { useState } from 'react';
import { EditOutlined } from '@mui/icons-material';
import { Box, CircularProgress, Link, Stack, TextField, Typography } from '@mui/material';
import tokens from '../shared/theme/tokens';

/**
 * View/edit toggle for a single value (email, phone, override amount).
 * Manages its own edit state; `onSave` may return a promise — the field
 * stays in edit mode with a spinner until it resolves, and exits on success.
 *
 * Props:
 * - value: current value (view mode display + edit seed)
 * - onSave(newValue): called on save; may return a promise
 * - renderValue?(value): custom view-mode rendering
 * - disabled: hides the edit affordance
 * - textFieldProps: spread onto the TextField (type, inputProps, ...)
 */
export default function InlineEditField({ value, onSave, renderValue, disabled = false, textFieldProps }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    setDraft(value ?? '');
    setEditing(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await onSave(draft);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <Stack direction="row" alignItems="center" gap={0.75} sx={{ minWidth: 0 }}>
        {renderValue ? (
          renderValue(value)
        ) : (
          <Typography variant="body2" sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {value}
          </Typography>
        )}
        {!disabled && (
          <Box
            component="span"
            onClick={startEdit}
            role="button"
            aria-label="Edit"
            sx={{ cursor: 'pointer', display: 'inline-flex', color: tokens.colors.inkFaint, '&:hover': { color: tokens.colors.primary } }}
          >
            <EditOutlined sx={{ fontSize: 16 }} />
          </Box>
        )}
      </Stack>
    );
  }

  return (
    <Stack direction="row" alignItems="center" gap={1}>
      <TextField size="small" value={draft} onChange={e => setDraft(e.target.value)} disabled={saving} {...textFieldProps} />
      {saving ? (
        <CircularProgress size={16} />
      ) : (
        <>
          <Link component="button" type="button" variant="body2" onClick={handleSave} sx={{ fontWeight: 600 }}>
            Save
          </Link>
          <Link component="button" type="button" variant="body2" color="text.secondary" onClick={() => setEditing(false)}>
            Cancel
          </Link>
        </>
      )}
    </Stack>
  );
}
